import { logger } from '@adornis/base/logging.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '@adornis/buchungsheld-db/server/scoped-collection.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { context } from '@adornis/baseql/server/context.js';
import { NotLoggedInError } from '@adornis/users/db/errors.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MatchingWeekPermissions, MatchingWeekRole } from '../db/enums/roles.js';
import { JobApplication } from '../db/job-application.js';
import { JobPosition } from '../db/job-position.js';
import { MatchingScore } from '../db/MatchingScore.js';
import { MatchingScoreCache } from '../db/MatchingScoreCache.js';
import { MWUserData } from '../db/user.js';
import { getAndEnsureUserDataOfUserID } from './user.js';
import { getAllJobPositionsAll, getJobPositionByID } from './job-position.js';

/**
 * Calculates a matching score for a given user and job position. (a matching score is uniquely identifiable by userID & jobPositionID)
 * If the score is already cached, it returns the cached score.
 * Otherwise, it calculates the score and caches it.
 * Usage:
 * - In resolved field `matchingScore` of JobApplication
 * - On pages where the user sees their matching score for a job position
 * - On pages where company users see applicants and their respective matching scores
 *
 * @param userID The ID of the user.
 * @param jobPositionID The ID of the job position.
 * @returns The matching score.
 */
export const getOrCalculateMatchingScore = registerMutation({
  operationName: 'getOrCalculateMatchingScore',
  resolve: (userID: string, jobPositionID: string) => async (gqlFields: BaseQLSelectionSet<MatchingScore>) => {
    let scoringUserID = userID;
    if (!context.serverContext) {
      const caller = await context.user();
      if (!caller) throw new NotLoggedInError();
      const isAdmin = caller.roles?.some(role => role.name === MatchingWeekRole.ADMIN) ?? false;
      if (caller._id === userID) {
        scoringUserID = caller._id;
      } else if (!isAdmin) {
        const jobPositionCollection = mongo().collection<EntityData<JobPosition>>(JobPosition._collectionName);
        const jobPosition = await jobPositionCollection.findOne(
          { _id: jobPositionID },
          { projection: { companyID: 1 } },
        );
        const applicationCollection = mongo().collection<EntityData<JobApplication>>(JobApplication._collectionName);
        const application = await applicationCollection.findOne(
          { userId: userID, jobPositionId: jobPositionID },
          { projection: { _id: 1 } },
        );
        if (
          !application ||
          !jobPosition?.companyID ||
          !caller.hasPermission(MatchingWeekPermissions.MODIFY_COMPANY, jobPosition.companyID)
        ) {
          throw new Error('Not authorized to calculate this matching score');
        }
      }
    }

    //* Check cache for existing entry
    const matchingScoreCacheCol = await scopedCollection(MatchingScoreCache);
    const cachedMatchingScore = await matchingScoreCacheCol.findOne({ userID: scoringUserID, jobPositionID });
    if (cachedMatchingScore?.matchingScore) return cachedMatchingScore.matchingScore;

    //* If not cached, calculate and persist
    //* Calculate
    // Load required data
    const [userData, job] = await Promise.all([
      getAndEnsureUserDataOfUserID(scoringUserID)(selectionSet(() => MWUserData, 2)),
      getJobPositionByID(jobPositionID)(selectionSet(() => JobPosition, 2)),
    ]);

    // dont calculate anything if we haven't set any personal information
    // university is a nonoptional field that we use to determine whether this has been saved once by the user (validators would have been checked)
    if (!job || !userData || !userData.university) return null;

    // Calculate score (dynamic import to keep server-only deps out of client bundle)
    const { calculateMatchingScore } = await import('../features/matching-score/api/index.js');
    const calculationStartedAt = Date.now();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const result = await (async () => {
      try {
        const calculationResult = await Promise.race([
          calculateMatchingScore(userData, job as JobPosition),
          new Promise<never>((_resolve, reject) => {
            timeout = setTimeout(() => {
              logger.warn({ userID: scoringUserID, jobPositionID }, 'matching score calculation timed out');
              reject(new Error('matching score calculation timed out'));
            }, 90_000);
          }),
        ]);
        const durationMs = Date.now() - calculationStartedAt;
        if (durationMs > 30_000) {
          logger.warn(
            { userID: scoringUserID, jobPositionID, durationMs },
            'matching score calculation took longer than 30s',
          );
        }
        return calculationResult;
      } finally {
        if (timeout) clearTimeout(timeout);
      }
    })();

    // Map to entity and persist on application
    const matchingScore = new MatchingScore({
      total: result.total,
      weightedSkills: result.weighted.skills,
      weightedLanguages: result.weighted.languages,
      weightedSoftSkills: result.weighted.softSkills,
      weightedExperience: result.weighted.experience,
      weightedPosition: result.weighted.position,
      rawSkills: result.raw.skills,
      rawLanguages: result.raw.languages,
      rawSoftSkills: result.raw.softSkills,
      rawExperience: result.raw.experience,
      rawPosition: result.raw.position,
      notes: result.notes,
    });

    //* Persist
    const cacheEntry = new MatchingScoreCache({
      userID: scoringUserID,
      jobPositionID: jobPositionID,
      matchingScore,
      createdAt: new Date(),
      updatedAt: new Date(),
    }).toObject();
    // toObject() yields _id: null for a never-persisted entity; replacing with an explicit null _id
    // corrupts the collection (and a second upsert dies on the _id_ index). Let Mongo assign it.
    if (cacheEntry._id == null) delete (cacheEntry as Partial<typeof cacheEntry>)._id;
    const rawMatchingScoreCacheCol = mongo().collection<EntityData<MatchingScoreCache>>(
      MatchingScoreCache._collectionName,
    );
    // $setOnInsert keeps the original createdAt when a concurrent computation overwrites the row.
    const { createdAt, ...cacheEntryWithoutCreatedAt } = cacheEntry;
    await rawMatchingScoreCacheCol.updateOne(
      { userID: scoringUserID, jobPositionID },
      { $set: cacheEntryWithoutCreatedAt, $setOnInsert: { createdAt } },
      { upsert: true },
    );
    return matchingScore;
  },
  params: [
    { name: 'userID', type: () => String },
    { name: 'jobPositionID', type: () => String },
  ],
  type: () => MatchingScore,
});

export const recommendedJobPositions = registerQuery({
  operationName: 'recommendedJobPositions',
  resolve: (userID: string) => async (gqlFields: BaseQLSelectionSet<JobPosition>) => {
    if (!context.serverContext) {
      const caller = await context.user();
      if (!caller) throw new NotLoggedInError();
      userID = caller._id;
    }
    // Get all jobs
    const jobs = await getAllJobPositionsAll()(selectionSet(() => JobPosition, 2));

    // Get all job applications for this user
    const jobApplicationCollection = await scopedCollection(JobApplication);
    const userApplications = await jobApplicationCollection.find({ userId: userID }).toArray();

    // Create a set of job IDs the user has already applied to for faster lookup
    const appliedJobIds = new Set(userApplications.map(app => app.jobPositionId));

    // Filter jobs to only include those the user hasn't applied to yet
    const availableJobs = jobs.filter(job => !appliedJobIds.has(job._id));

    // Calculate matching scores for available jobs in batches
    const BATCH_SIZE = 10;
    const matchingScores: { job: JobPosition; score: number }[] = [];

    // Process jobs in batches for more efficient processing
    for (let i = 0; i < availableJobs.length; i += BATCH_SIZE) {
      const batch = availableJobs.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(job =>
        getOrCalculateMatchingScore(
          userID,
          job._id,
        )(selectionSet(() => MatchingScore, 2)).then(score => ({ job: job as JobPosition, score: score?.total ?? 0 })),
      );

      const batchResults = await Promise.all(batchPromises);
      matchingScores.push(...batchResults);
    }

    // Sort by score and return top 12
    return matchingScores
      .sort((a, b) => b.score - a.score)
      .map(a => a.job)
      .slice(0, 12);
  },
  params: [{ name: 'userID', type: () => String }],
  type: () => [JobPosition],
});
